import React from 'react';
import { X, Printer, Receipt, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { overlayClose } from '../lib/overlayClose';
import { printHtmlSilently } from '../lib/silentPrint';
import { valorPorExtenso } from '../lib/extenso';

const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const buildChargeHtml = ({ company, customer, notes, total }) => {
  const rows = notes.map((n) => `
    <tr>
      <td>${n.sale_number != null ? '#' + esc(n.sale_number) : '—'}</td>
      <td>${fmtDate(n.created_at)}</td>
      <td>${fmtDate(n.due_date)}${n.days_overdue > 0 ? '*' : ''}</td>
      <td style="text-align:right">${fmt(n.remaining)}</td>
    </tr>`).join('');
  const hasOverdue = notes.some((n) => n.days_overdue > 0);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Cobrança ${esc(customer.name)}</title>
    <style>
      @page { size: 80mm auto; margin: 2mm; }
      * { font-family: 'Courier New', monospace; }
      body { width: 76mm; font-size: 11px; color: #000; margin: 0; }
      h2 { text-align:center; font-size: 13px; margin: 4px 0; }
      .center { text-align:center; } .bold { font-weight: bold; } .big { font-size: 14px; }
      table { width:100%; border-collapse: collapse; margin-top: 4px; }
      th, td { padding: 1px 0; font-size: 10px; }
      th { border-bottom: 1px dashed #000; text-align:left; }
      .divider { border-top: 1px dashed #000; margin: 4px 0; }
      .row { display:flex; justify-content:space-between; }
      .small { font-size: 10px; }
    </style></head><body>
    <h2>${esc(company?.trade_name || company?.legal_name || 'Smart Flow')}</h2>
    ${company?.cnpj ? `<div class="center small">CNPJ: ${esc(company.cnpj)}</div>` : ''}
    ${company?.address ? `<div class="center small">${esc(company.address)}</div>` : ''}
    ${company?.phone ? `<div class="center small">Fone: ${esc(company.phone)}</div>` : ''}
    <div class="divider"></div>
    <div class="center bold big">COBRANCA</div>
    <div class="center small">Venda a prazo - notas em aberto</div>
    <div class="divider"></div>
    <div class="bold">Cliente: ${esc(customer.name)}</div>
    ${customer.cpf_cnpj ? `<div>Doc: ${esc(customer.cpf_cnpj)}</div>` : ''}
    ${customer.phone ? `<div>Fone: ${esc(customer.phone)}</div>` : ''}
    <div>Emitido: ${new Date().toLocaleString('pt-BR')}</div>
    <div class="divider"></div>
    <table>
      <thead><tr><th>Nota</th><th>Data</th><th>Venc.</th><th style="text-align:right">Saldo</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    ${hasOverdue ? '<div class="small">* nota vencida</div>' : ''}
    <div class="divider"></div>
    <div class="row"><span>Notas selecionadas:</span><span>${notes.length}</span></div>
    <div class="row bold big"><span>TOTAL A PAGAR:</span><span>${fmt(total)}</span></div>
    <div class="small">(${esc(valorPorExtenso(total))})</div>
    ${company?.pix_key ? `<div class="divider"></div><div class="bold">Pague via PIX:</div><div>Chave: ${esc(company.pix_key)}</div>${company.pix_merchant_name ? `<div>Favorecido: ${esc(company.pix_merchant_name)}</div>` : ''}` : ''}
    <div class="divider"></div>
    <br/>
    <div class="center">______________________________</div>
    <div class="center small">Assinatura do cliente</div>
    <div class="divider"></div>
    <div class="center small">Este documento nao e boleto bancario.</div>
    <div class="center small">Pagamento na loja ou via PIX.</div>
    <br/>
    </body></html>`;
};

export const ChargeModal = ({ company, customer, notes, onClose }) => {
  const total = notes.reduce((s, n) => s + Number(n.remaining || 0), 0);
  const overdue = notes.filter((n) => n.days_overdue > 0).length;

  const handlePrint = () => printHtmlSilently(buildChargeHtml({ company, customer, notes, total }));

  const handleWhatsApp = () => {
    const lines = [
      `*${company?.trade_name || 'Smart Flow'}* — Cobrança`,
      `Cliente: ${customer.name}`,
      '',
      ...notes.map((n) => `• Nota #${n.sale_number ?? '—'} de ${fmtDate(n.created_at)} — saldo ${fmt(n.remaining)}${n.days_overdue > 0 ? ' (vencida)' : ''}`),
      '',
      `*TOTAL A PAGAR: ${fmt(total)}*`,
      company?.pix_key ? `PIX: ${company.pix_key}` : '',
    ].filter((l) => l !== undefined);
    const num = (customer.phone || '').replace(/\D/g, '');
    const full = num ? (num.startsWith('55') ? num : `55${num}`) : '';
    window.open(`https://wa.me/${full}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" {...overlayClose(onClose)} data-testid="charge-modal">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1"><Receipt size={12} /> Cobrança</p>
            <h2 className="text-xl font-black text-slate-900" data-testid="charge-customer-name">{customer.name}</h2>
            <p className="text-sm text-slate-500">{notes.length} nota(s) selecionada(s){overdue > 0 && <span className="text-red-600 font-bold"> · {overdue} vencida(s)</span>}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 p-1" data-testid="charge-close"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <table className="w-full text-sm" data-testid="charge-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Nota</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Data</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Vencimento</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-slate-500">Saldo devedor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {notes.map((n) => (
                <tr key={n.id} data-testid={`charge-row-${n.id}`}>
                  <td className="px-3 py-2 font-mono font-bold">{n.sale_number != null ? `#${n.sale_number}` : '—'}</td>
                  <td className="px-3 py-2">{fmtDate(n.created_at)}</td>
                  <td className="px-3 py-2">{fmtDate(n.due_date)}{n.days_overdue > 0 && <span className="block text-[10px] font-bold text-red-600">{n.days_overdue} dia(s) em atraso</span>}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-red-700 whitespace-nowrap">{fmt(n.remaining)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-900 text-white">
                <td colSpan={3} className="px-3 py-3 text-right text-xs uppercase font-bold">Total geral a pagar</td>
                <td className="px-3 py-3 text-right font-mono font-black text-lg whitespace-nowrap" data-testid="charge-total">{fmt(total)}</td>
              </tr>
            </tfoot>
          </table>
          <p className="text-xs text-slate-500 italic mt-2">({valorPorExtenso(total)})</p>
        </div>

        <div className="p-4 border-t border-slate-200 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={handleWhatsApp} data-testid="charge-whatsapp-btn"><MessageCircle size={16} className="mr-2" /> Enviar WhatsApp</Button>
          <Button onClick={handlePrint} className="bg-slate-900 hover:bg-black" data-testid="charge-print-btn"><Printer size={16} className="mr-2" /> Imprimir cobrança</Button>
        </div>
      </div>
    </div>
  );
};
